/**
* IBM Confidential
* Licensed Materials - Property of IBM
* (C) Copyright IBM Corp. 2020, 2024 All Rights Reserved.
* US Government Users Restricted Rights - Use, duplication or
* disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
* */

import { objectWithoutProperties as _objectWithoutProperties, objectSpread2 as _objectSpread2 } from '../../_virtual/_rollupPluginBabelHelpers.js';

var _excluded = ["toAttach"],
  _excluded2 = ["createTag"],
  _excluded3 = ["createTag"],
  _excluded4 = ["createTag"],
  _excluded5 = ["toAttach", "errors"],
  _excluded6 = ["toDetach", "errors"];
/* exporting to simplify testing coverage */
var windowObj = typeof window !== 'undefined' ? window : {};
function getAccountIds() {
  var header = windowObj.header;
  var accountId;
  if (header) {
    var storedAccountData = header.storedAccountData;
    if (storedAccountData) {
      accountId = storedAccountData.selectedAccountGuid;
      if (storedAccountData.accounts && storedAccountData.accounts.length > 0) {
        var accounts = storedAccountData.accounts;
        var account = accounts.find(function (acc) {
          return acc.accountGuid === accountId;
        });
        if (account) {
          var softlayerAccountId = account.softlayerAccountId,
            contextId = account.contextId;
          return {
            accountId: accountId,
            softlayerAccountId: softlayerAccountId,
            contextId: contextId
          };
        }
      }
    }
  }
  return {
    accountId: accountId,
    softlayerAccountId: undefined
  };
}
function getDataProviders(tagType) {
  var _windowObj$location;
  var providers = 'ghost';
  if (tagType && tagType === 'access') {
    return providers; // GhoST is the only supported data provider for access tags
  }
  var _getAccountIds = getAccountIds(),
    softlayerAccountId = _getAccountIds.softlayerAccountId;
  var env = windowObj.aceProxyUrl || (windowObj === null || windowObj === void 0 || (_windowObj$location = windowObj.location) === null || _windowObj$location === void 0 ? void 0 : _windowObj$location.hostname);
  var isProduction = !!env && !(env.indexOf('localhost') >= 0 || env.indexOf('test') >= 0);
  // we only want to get ims tags if it's a softlayer account and we are in production env.
  // otherwise we will get an error. IMS token is invalid in test/stage env for tagging.
  if (isProduction && softlayerAccountId) {
    providers = 'ghost,ims';
  }
  return providers;
}
function request(obj) {
  var _getAccountIds2 = getAccountIds(),
    accountId = _getAccountIds2.accountId,
    softlayerAccountId = _getAccountIds2.softlayerAccountId,
    contextId = _getAccountIds2.contextId;
  var _obj$method = obj.method,
    method = _obj$method === void 0 ? 'GET' : _obj$method,
    body = obj.body,
    url = obj.url,
    _obj$headers = obj.headers,
    _obj$headers2 = _obj$headers === void 0 ? {} : _obj$headers,
    xCorrelationID = _obj$headers2['X-Correlation-ID'],
    xRequestID = _obj$headers2['X-Request-ID'];
  var headers = {
    'Content-Type': 'application/json',
    Accept: 'application/json'
  };
  if (xCorrelationID) {
    headers['X-Correlation-ID'] = xCorrelationID;
  }
  if (xRequestID) {
    headers['X-Request-ID'] = xRequestID;
  }
  headers['X-BSS-Account'] = accountId;
  if (softlayerAccountId) {
    headers['X-IMS-Account'] = softlayerAccountId;
  }
  if (contextId) {
    headers['IBM-Account-Context'] = contextId;
  }
  var fetchUrl = windowObj.aceProxyUrl + url;
  var options = {
    headers: headers,
    method: method,
    credentials: 'include'
  };
  if (body) {
    options.body = JSON.stringify(body);
  }
  return new Promise(function (resolve) {
    fetch(fetchUrl, options).then(function (response) {
      if (response.status >= 200 && response.status < 400) {
        return response.json();
      }
      throw new Error("".concat(response.status, " ").concat(response.statusText));
    }).then(function (jsonResponse) {
      resolve(jsonResponse);
    })["catch"](function (error) {
      var message = error.message;
      var err = {
        success: false,
        error: {
          message: message
        }
      };
      resolve(err);
    });
  });
}
function createAccessTagsRequest(requestObj) {
  var toAttach = requestObj.toAttach,
    rest = _objectWithoutProperties(requestObj, _excluded);
  var accessTags = toAttach.filter(function (tag) {
    return tag.tag_type === 'access';
  });
  var userTags = toAttach.filter(function (tag) {
    return tag.tag_type !== 'access';
  });
  var accessTagsToCreate = accessTags.filter(function (tag) {
    return tag.createTag;
  });
  var newAttach = [];
  if (accessTagsToCreate.length === 0) {
    // Remove createTag prop
    newAttach = toAttach.map(function (tag) {
      tag.createTag;
        var tagProps = _objectWithoutProperties(tag, _excluded2);
      return tagProps;
    });
    return Promise.resolve(_objectSpread2({
      toAttach: newAttach,
      errors: []
    }, rest));
  }
  return new Promise(function (resolve) {
    request({
      url: '/account/api/tagging/v3/tags?tag_type=access',
      method: 'POST',
      body: {
        tags: accessTagsToCreate
      },
      headers: rest.headers
    }).then(function (result) {
      var newAccessAttach;
      var errors = [];
      if (!Array.isArray(result)) {
        newAccessAttach = accessTags.filter(function (tag) {
          return !tag.createTag;
        });
        errors = [(result === null || result === void 0 ? void 0 : result.error) || result];
      } else {
        errors = result.filter(function (tag) {
          return tag.is_error;
        });
        newAccessAttach = accessTags.filter(function (tag) {
          return errors.every(function (errorTag) {
            return tag.name !== errorTag.tag_name;
          });
        });
      }
      newAttach = userTags.concat(newAccessAttach);
      // Remove createTag prop
      newAttach = newAttach.map(function (tag) {
        tag.createTag;
          var tagProps = _objectWithoutProperties(tag, _excluded3);
        return tagProps;
      });
      resolve(_objectSpread2({
        toAttach: newAttach,
        errors: errors
      }, rest));
    })["catch"](function (error) {
      var newAccessAttach = accessTags.filter(function (tag) {
        return !tag.createTag;
      });
      newAttach = userTags.concat(newAccessAttach);
      newAttach = newAttach.map(function (tag) {
        tag.createTag;
          var tagProps = _objectWithoutProperties(tag, _excluded4);
        return tagProps;
      });
      resolve(_objectSpread2({
        toAttach: newAttach,
        errors: [error]
      }, rest));
    });
  });
}

/**
 * Makes requests to the tagging API to attach tags.
 * @param {object} requestObj The object to pass through to attach tags.
 * @param {string} requestObj.crn The CRN of a resource to attach tags to.
 * @param {array} requestObj.toAttach An array of tags to attach.
 * @param {array} requestObj.errors An array of errors. Useful for promise chains.
 */
function attachTagsRequest(requestObj) {
  var toAttach = requestObj.toAttach,
    _requestObj$errors = requestObj.errors,
    errors = _requestObj$errors === void 0 ? [] : _requestObj$errors,
    rest = _objectWithoutProperties(requestObj, _excluded5);
  if (toAttach.length === 0) {
    return Promise.resolve(_objectSpread2({
      attached: [],
      errors: errors
    }, rest));
  }
  var tags = toAttach;
  var resource = requestObj.resource;
  return new Promise(function (resolve) {
    request({
      url: '/account/api/tagging/v3/attach',
      method: 'POST',
      body: {
        tags: tags,
        resources: [resource]
      },
      headers: rest.headers
    }).then(function (result) {
      var errorList = Array.isArray(result) ? result.filter(function (item) {
        return item.success === false;
      }) : [result];
      Array.prototype.push.apply(errors, errorList);
      var attached = Array.isArray(result) ? result.filter(function (item) {
        return item.success === true;
      }) : [];
      resolve(_objectSpread2({
        attached: attached,
        errors: errors
      }, rest));
    });
  });
}

/**
 * Makes requests to the tagging API to detach tags.
 * @param {object} requestObj The object to pass through to attach tags.
 * @param {string} requestObj.crn The CRN of a resource to detach tags from.
 * @param {array} requestObj.toDetach An array of tags to detach.
 * @param {array} requestObj.errors An array of errors. Useful for promise chains.
 */
function detachTagsRequest(requestObj) {
  var toDetach = requestObj.toDetach,
    _requestObj$errors2 = requestObj.errors,
    errors = _requestObj$errors2 === void 0 ? [] : _requestObj$errors2,
    rest = _objectWithoutProperties(requestObj, _excluded6);
  if (toDetach.length === 0) {
    return Promise.resolve(_objectSpread2({
      detached: [],
      errors: errors
    }, rest));
  }
  var tags = toDetach;
  var resource = requestObj.resource;
  return new Promise(function (resolve) {
    request({
      url: '/account/api/tagging/v3/detach',
      method: 'POST',
      body: {
        tags: tags,
        resources: [resource]
      },
      headers: rest.headers
    }).then(function (result) {
      var errorList = Array.isArray(result) ? result.filter(function (item) {
        return item.success === false;
      }) : [result];
      Array.prototype.push.apply(errors, errorList);
      var detached = Array.isArray(result) ? result.filter(function (item) {
        return item.success === true;
      }) : [];
      resolve(_objectSpread2({
        detached: detached,
        errors: errors
      }, rest));
    });
  });
}

/**
 * Makes requests to the tagging API to create, attach, and detach tags.
 * @param {object} requestObj The object to pass through to attach tags.
 * @param {string} requestObj.resource  The resource to attach and/or detach tags to. {id:"crn:fakecrn"}
 * @param {array} requestObj.toAttach An array of tags to attach and create. [{name:'tag1'},{name:'tag2'}]
 * @param {array} requestObj.toDetach An array of tags to detach. [{name:'tag1'},{name:'tag2'}]
 */

/*
  tagRequests({
      toAttach: [{name:'tag1', tag_type: 'access'},{name:'tag2', createTag: true},{name:'tag4', createTag: true, tag_type: 'access'}],
      toDetach: [{name:'tag3'}, {name:'tag5', tag_type: 'access'}],
      resource: {
        id: '1234543',
        type: 'Softlayer_Metal_Type' //softlayer resources must list their types
      },
    }
 */
function tagRequests(requestObj) {
  var _requestObj$toAttach = requestObj.toAttach,
    toAttach = _requestObj$toAttach === void 0 ? [] : _requestObj$toAttach,
    _requestObj$toDetach = requestObj.toDetach,
    toDetach = _requestObj$toDetach === void 0 ? [] : _requestObj$toDetach;
  var formatedRequestObj = _objectSpread2(_objectSpread2({}, requestObj), {}, {
    toAttach: toAttach,
    toDetach: toDetach
  });
  return createAccessTagsRequest(formatedRequestObj).then(attachTagsRequest).then(detachTagsRequest);
}
var getFetchOptions = function getFetchOptions(accountId, mock, open, resourceId, supportAccessTags) {
  var isSoftLayerResource = !/^crn/.test(resourceId);
  return {
    auth: function auth() {
      return {
        options: {
          refetchOnMount: true,
          templateKeys: [accountId],
          queryParams: {
            resourceId: resourceId
          },
          mock: mock,
          skip: !open || !accountId || isSoftLayerResource
        }
      };
    },
    userTags: function userTags() {
      return {
        options: {
          refetchOnMount: true,
          queryParams: {
            providers: getDataProviders()
          },
          mock: mock,
          skip: !open
        }
      };
    },
    accessTags: function accessTags() {
      return {
        options: {
          refetchOnMount: true,
          queryParams: {
            providers: getDataProviders('access'),
            tag_type: 'access'
          },
          mock: mock,
          skip: !open || !supportAccessTags
        }
      };
    },
    resourceTags: function resourceTags() {
      return {
        options: {
          refetchOnMount: true,
          templateKeys: [resourceId],
          mock: mock,
          skip: !open
        }
      };
    }
  };
};

export { attachTagsRequest, createAccessTagsRequest, detachTagsRequest, getAccountIds, getDataProviders, getFetchOptions, request, tagRequests };
